import { useState } from "react";
import { Button, Typography } from "@/components/atoms";
import { useGetAccessListQuery } from "@/redux/apis/assistants/assistantsApi";
import { AccessLink } from "@/redux/apis/assistants/types";
import { useAppSelector } from "@/redux/hooks";
import { configSelector } from "@/redux/slices/config/selectors";
import { ClientAvatar } from "./myClients/ClientAvatar";
import { formatRelativeShort } from "./myClients/helpers";
import { useClientScope } from "./settings/ClientScopeContext";
import { ReviewRequestModal } from "./settings/assistants/ReviewRequestModal";

/**
 * Home-screen "someone wants to be your assistant" pending-request rows (Figma 6285-16911).
 *
 * Shown to the CLIENT (their own Home) and to the OPERATOR viewing the client (Manage-Client
 * Details, where a `ClientScopeProvider` supplies the account id). NOT shown to Executive
 * Assistants:
 *  - an EA's own Home renders the "All Bookings" branch, which never mounts this component;
 *  - the EA's Manage-Client takeover doesn't render it either.
 * Scope + approve/decline follow `ClientScopeContext` (undefined `accountId` = the logged-in
 * client's own account), exactly like `ReviewRequestModal`, so an operator acts on the client's
 * behalf. Renders nothing when there are no pending requests.
 */
export const PendingAssistantRequests = () => {
  // Managed client id when an operator is viewing a client; undefined in the client's own portal.
  const { accountId } = useClientScope();
  const { data: pending = [] } = useGetAccessListQuery({ type: "MANAGES_YOU", status: "PENDING", accountId });
  const config = useAppSelector(configSelector);
  const operatorName = (config?.name || "your operator").replace(/\.+$/, "");
  const [reviewLink, setReviewLink] = useState<AccessLink | null>(null);

  if (pending.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {pending.map((link) => (
        <div
          key={link.id}
          className="flex animate-pulse-ring flex-col gap-3 rounded-lg border border-vivid-purple bg-white p-4 shadow-[0_0_0_4px_#E1D9F4] motion-reduce:animate-none sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex min-w-0 items-center gap-4">
            {/* Dashed ring marks the pending (not-yet-approved) assistant. */}
            <ClientAvatar
              name={link.party.name}
              avatar={link.party.avatar}
              size="md"
              className="border-2 border-dashed border-vivid-purple"
            />
            <div className="min-w-0">
              <Typography variant="action" className="block truncate font-semibold text-neutral-black">
                {link.party.name || link.party.email} wants to be {accountId ? "this client's" : "your"} assistant on {operatorName}
              </Typography>
              <Typography variant="paragraph" className="block truncate text-neutral-silver-gray">
                {link.party.email}
                {formatRelativeShort(link.createdAt) && <span> • Requested {formatRelativeShort(link.createdAt)} ago</span>}
              </Typography>
            </div>
          </div>
          <Button
            variant="custom"
            size="sm"
            className="shrink-0 bg-success text-white hover:bg-success/90"
            onClick={() => setReviewLink(link)}
          >
            Review Request
          </Button>
        </div>
      ))}

      <ReviewRequestModal link={reviewLink} open={!!reviewLink} onClose={() => setReviewLink(null)} />
    </div>
  );
};
